import { DEFAULT_TIMEZONE } from "@/lib/constants";
import { toDayKey } from "@/utils/format-datetime";

/**
 * How far ahead a shift still counts as "soon" — i.e. worth showing as a countdown ("over 3 uur")
 * instead of a calendar reference ("morgen 18:00"). Twelve hours means a late shift is a countdown
 * from the moment you wake up, and an early shift is a countdown from the evening before.
 */
export const SOON_THRESHOLD_MINUTES = 12 * 60;

export type ShiftTimingState =
  /** Started, not finished. */
  | "running"
  /** Starts within {@link SOON_THRESHOLD_MINUTES}. */
  | "soon"
  /** Starts later on the same calendar day (Amsterdam time). */
  | "today"
  /** Starts on the next calendar day. */
  | "tomorrow"
  /** Starts further out. */
  | "later"
  /** Already finished. */
  | "ended";

export interface ShiftTiming {
  state: ShiftTimingState;
  /** Minutes until the shift starts. Negative once it has started. */
  minutesUntilStart: number;
  /** Minutes until the shift ends. Negative once it has ended. */
  minutesUntilEnd: number;
  /** Calendar days between today and the start day, in Amsterdam time. 0 = today, 1 = tomorrow. */
  daysUntilStart: number;
}

/**
 * The calendar day of an instant, expressed in Amsterdam time as a whole number of days.
 * Subtracting two of these gives a real calendar-day difference, which is what "morgen" means to a
 * user — a shift starting at 00:30 tonight is tomorrow, even though it is only a few hours away.
 */
function amsterdamDayNumber(instant: string | Date): number {
  // Via de dagsleutel en niet via `getFullYear()/getMonth()/getDate()`: die velden lezen de klok
  // van het toestel, en tempo's `tzDate` telde de afwijking er nog een tweede keer bij op.
  const [year, month, day] = toDayKey(instant, DEFAULT_TIMEZONE).split("-").map(Number);
  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
}

function minutesBetween(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / 60_000);
}

/**
 * Classifies a shift relative to now, so the UI can pick between a countdown, a day reference and a
 * plain date without re-deriving the rules per component.
 */
export function getShiftTiming(
  startAt: string | Date,
  endAt: string | Date,
  now: Date = new Date(),
): ShiftTiming {
  const start = typeof startAt === "string" ? new Date(startAt) : startAt;
  const end = typeof endAt === "string" ? new Date(endAt) : endAt;

  const minutesUntilStart = minutesBetween(now, start);
  const minutesUntilEnd = minutesBetween(now, end);
  const daysUntilStart = amsterdamDayNumber(start) - amsterdamDayNumber(now);

  const state: ShiftTimingState = (() => {
    if (minutesUntilEnd <= 0) {
      return "ended";
    }
    if (minutesUntilStart <= 0) {
      return "running";
    }
    if (minutesUntilStart <= SOON_THRESHOLD_MINUTES) {
      return "soon";
    }
    if (daysUntilStart === 0) {
      return "today";
    }
    if (daysUntilStart === 1) {
      return "tomorrow";
    }
    return "later";
  })();

  return { state, minutesUntilStart, minutesUntilEnd, daysUntilStart };
}

/**
 * Splits a minute count for display. Under an hour we show minutes, because "over 45 min" is
 * actionable and "over 0 uur" is not; from an hour up we show whole hours, because nobody leaves
 * the house on a two-minute distinction.
 */
export function splitMinutes(totalMinutes: number): { hours: number; minutes: number } {
  const safe = Math.max(0, totalMinutes);
  return { hours: Math.floor(safe / 60), minutes: safe % 60 };
}
